import { DefaultLabField } from "@/services/backend/samples/components/sample-base-data-default-lab-field";
import { Kind } from "@/services/backend/samples/types/kind";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  PatchRequest,
  updateSamplesSurfaceMicrobialBaseDataCache,
  useSamplesSurfaceMicrobialBaseDataPatchMutation,
  useSamplesSurfaceMicrobialBaseDataShowQuery,
} from "@/services/backend/samples/api/surface-microbial-base-data-api";
import { SurfaceMicrobialBaseData } from "@/services/backend/samples/types/sample-base-data";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import {
  NumberInputField,
  TextInputField,
} from "@/shared/components/field/input-field";
import { CheckboxField } from "@/services/backend/samples/components/sample-base-data-checkbox-field";

/**
 * The patch carrying every editable value. Sending all of them — not just
 * the changed one — keeps a quick edit of a second field from replacing
 * the pending patch of the first one before it is sent.
 */
function patchOf(data: SurfaceMicrobialBaseData): PatchRequest {
  return {
    id: data.id,
    label: data.label,
    unit: data.unit,
    sampleUnit: data.sampleUnit,
    warningFrom: data.warningFrom,
    criticalAbove: data.criticalAbove,
    dg18: data.dg18,
    labId: data.labId,
  };
}

export function SurfaceMicrobialBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useSamplesSurfaceMicrobialBaseDataShowQuery({ id });

  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateSamplesSurfaceMicrobialBaseDataCache,
    useSamplesSurfaceMicrobialBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }

  if (dataLoading || !data) {
    return <CardContentSkeleton />;
  }

  const edit = (change: Partial<SurfaceMicrobialBaseData>) => {
    const next = { ...data, ...change };
    set(patchOf(next), next);
  };

  return (
    <div className="grid gap-4">
      {/* The label is unique per company, so a rename can be rejected
          with a conflict rather than a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => edit({ label })}
        errors={inputValidationErrorMessages(error, "label")}
      />
      <TextInputField
        label={t("Einheit (Satz)")}
        text={data.unit}
        onTextChange={(unit) => edit({ unit })}
        errors={inputValidationErrorMessages(error, "unit")}
      />
      <TextInputField
        label={t("Einheit (Probe)")}
        text={data.sampleUnit}
        onTextChange={(sampleUnit) => edit({ sampleUnit })}
        errors={inputValidationErrorMessages(error, "sampleUnit")}
      />
      <NumberInputField
        label={t("Grenzwertig ab (Gesamtkeimzahl)")}
        description={t(
          "Ab dieser Gesamtkeimzahl wird ein Probensatz als grenzwertig beurteilt.",
        )}
        number={data.warningFrom}
        onNumberChange={(warningFrom) =>
          edit({ warningFrom: Math.max(0, warningFrom) })
        }
        min={0}
        errors={inputValidationErrorMessages(error, "warningFrom")}
      />
      <NumberInputField
        label={t("Unzureichend über (Gesamtkeimzahl)")}
        description={t(
          "Über dieser Gesamtkeimzahl wird ein Probensatz als unzureichend beurteilt. 0 bedeutet: keine automatische Beurteilung.",
        )}
        number={data.criticalAbove}
        onNumberChange={(criticalAbove) =>
          edit({ criticalAbove: Math.max(0, criticalAbove) })
        }
        min={0}
        errors={inputValidationErrorMessages(error, "criticalAbove")}
      />
      <CheckboxField
        label={t("DG18-Probe nehmen")}
        description={t(
          "Neben den Caso- und Malz-Proben wird eine DG18-Probe genommen.",
        )}
        checked={data.dg18}
        onCheckedChange={(dg18) => edit({ dg18 })}
        errors={inputValidationErrorMessages(error, "dg18")}
      />
      <DefaultLabField
        kind={Kind.SurfaceMicrobial}
        labId={data.labId}
        onLabIdChange={(labId) => edit({ labId })}
        errors={inputValidationErrorMessages(error, "labId")}
      />
    </div>
  );
}
